import { Link, useLocation } from "react-router-dom";
import { Bell, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useNotificationBadge } from "@/hooks/useNotifications";
import { cn } from "@/lib/utils";

/**
 * Navbar notification bell with unread badge.
 * Only renders when the user is logged in (the parent should gate on activeAccount).
 */
export function NavBarNotificationBadge() {
  const { unreadCount, loading } = useNotificationBadge();
  const location = useLocation();
  const isActive = location.pathname === "/notifications";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          asChild
          className={cn("h-8 w-8 relative", isActive && "bg-accent")}
        >
          <Link
            to="/notifications"
            aria-label={
              loading ? "Notifications, loading activity" : "Notifications"
            }
            aria-busy={loading}
          >
            <Bell className="h-4 w-4" />
            {loading && (
              <Loader2
                aria-hidden="true"
                className="absolute -bottom-1 -left-1 h-3 w-3 motion-safe:animate-spin"
              />
            )}
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-pink-500 px-1 text-[10px] font-medium text-white leading-none">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </Link>
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {loading
          ? "Loading notifications…"
          : unreadCount > 0
            ? `${unreadCount} unread notification${unreadCount === 1 ? "" : "s"}`
            : "Notifications"}
      </TooltipContent>
    </Tooltip>
  );
}
